import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Infinity } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ROLES = [
  { id: "owner", role: "buyer", label: "I'm an Owner" },
  { id: "seeker", role: "buyer", label: "Looking for a Boerboel" },
  { id: "breeder", role: "breeder", label: "I'm a Breeder" },
  { id: "business", role: "business", label: "I'm a Business" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer", account: "owner" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await register({ name: form.name, email: form.email, password: form.password, role: form.role }); toast.success("Account created"); nav("/dashboard"); }
    catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="grid lg:grid-cols-2 min-h-[80vh]">
        <div className="flex items-center justify-center p-8 order-2 lg:order-1">
          <div className="w-full max-w-sm">
            <h1 className="eon-heading text-4xl uppercase">Create Account</h1>
            <p className="text-sm text-eon-muted mt-2">Join E.O.N Infinity — free to start.</p>
            {error && <div className="mt-4 border border-red-800/50 bg-red-950/30 text-red-300 text-sm px-4 py-3" data-testid="register-error">{error}</div>}
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-eon-surface border border-eon-border px-4 py-3.5 focus:border-eon-gold outline-none" data-testid="register-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-eon-surface border border-eon-border px-4 py-3.5 focus:border-eon-gold outline-none" data-testid="register-email" />
              <input required type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-eon-surface border border-eon-border px-4 py-3.5 focus:border-eon-gold outline-none" data-testid="register-password" />
              <div>
                <div className="eon-label mb-2">How do you want to use E.O.N?</div>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map((r) => (
                    <button type="button" key={r.id} onClick={() => setForm({ ...form, role: r.role, account: r.id })} data-testid={`role-${r.id}`}
                      className={`text-xs py-3 px-2 border rounded-lg transition-colors ${form.account === r.id ? "bg-eon-gold text-white border-eon-gold" : "border-eon-border text-eon-text/70 hover:border-eon-gold"}`}>{r.label}</button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={busy} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3.5 hover:bg-eon-goldhover transition-colors disabled:opacity-50" data-testid="register-submit">{busy ? "Creating..." : "Create Account"}</button>
            </form>
            <div className="flex items-center gap-4 my-6"><div className="flex-1 h-px bg-eon-border" /><span className="eon-label">or</span><div className="flex-1 h-px bg-eon-border" /></div>
            <button onClick={google} data-testid="google-register-btn" className="w-full border border-eon-border py-3.5 text-sm font-medium hover:border-eon-gold transition-colors flex items-center justify-center gap-3">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
            </button>
            <p className="text-sm text-eon-muted mt-6 text-center">Have an account? <Link to="/login" className="text-eon-gold">Sign in</Link></p>
          </div>
        </div>
        <div className="relative hidden lg:flex flex-col justify-center items-center bg-eon-surface border-l border-eon-border p-12 order-1 lg:order-2">
          <span className="w-16 h-16 rounded-2xl bg-eon-gold flex items-center justify-center mb-8"><Infinity size={34} className="text-white" strokeWidth={2.75} /></span>
          <div className="eon-label mb-3">Get Started</div>
          <h2 className="eon-heading text-4xl uppercase text-eon-text text-center">Your E.O.N<br />Journey Starts Here</h2>
        </div>
      </div>
    </Layout>
  );
}
